// languagetools:disable
import * as Sentry from '@sentry/browser'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { IWorkspaceMember } from 'src/types/user'
import type { StreamChat, UserResponse } from 'stream-chat'

/**
 * Custom hook for fetching workspace users with retry logic
 * @param userDetails - User details containing the user ID
 * @param chatClientService - Stream Chat client instance
 * @param workspaceID - Current workspace ID
 * @param fetchWorkspaceMembers - Function to fetch workspace members from GraphQL
 * @returns Object containing mentionable users, loading state, error state, and refetch function
 */
export const useWorkspaceUsers = (
  userDetails: { id?: string },
  chatClientService: StreamChat,
  workspaceID: string,
  fetchWorkspaceMembers: () => Promise<{ data?: { workspaceMembers: IWorkspaceMember[] } }>,
  isWorkspacesLoading: boolean
): {
  error: string | null
  isLoading: boolean
  mentionableUsers: UserResponse[]
  refetch: () => void
  teamMembers: IWorkspaceMember[]
} => {
  const [mentionableUsers, setMentionableUsers] = useState<UserResponse[]>([])
  const [teamMembers, setTeamMembers] = useState<IWorkspaceMember[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const retryCountRef = useRef(0)
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)

  const resetRetryCount = useCallback(() => {
    retryCountRef.current = 0
    setError(null)
  }, [])

  const clearRetryTimeout = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
  }, [])

  const resetLocalState = useCallback(() => {
    setMentionableUsers([])
    setTeamMembers([])
    setError(null)
    setIsLoading(false)
  }, [])

  const fetchUsers = useCallback(
    async (retryCount = 0): Promise<void> => {
      // Validate dependencies
      if (!userDetails.id || !chatClientService || !workspaceID) {
        const missingDeps = {
          hasChatClient: !!chatClientService,
          hasUserDetails: !!userDetails.id,
          hasWorkspaceID: !!workspaceID
        }

        Sentry.captureMessage('fetchUsers: Missing dependencies', {
          extra: missingDeps
        })

        setError('Missing required dependencies')
        return
      }

      if (!isMountedRef.current) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        // Fetch workspace members
        const { data } = await fetchWorkspaceMembers()

        setTeamMembers(data?.workspaceMembers || [])
        const memberIDs = data?.workspaceMembers?.map((member) => member.userID)

        if (!memberIDs || memberIDs.length === 0) {
          setMentionableUsers([])
          return
        }

        if (!isMountedRef.current) {
          return
        }

        // Query users from Stream Chat
        const res = await chatClientService.queryUsers({ id: { $in: memberIDs } })

        if (isMountedRef.current) {
          setMentionableUsers(res.users)
          resetRetryCount()
        }
      } catch (error) {
        if (!isMountedRef.current) return

        // Handle Apollo store reset errors specifically
        if (error instanceof Error && error.message.includes('Store reset while query was in flight')) {
          // Retry with exponential backoff if under max retries
          if (retryCount < 2) {
            const delay = 500 * Math.pow(2, retryCount)
            retryTimeoutRef.current = setTimeout(() => {
              if (isMountedRef.current) {
                fetchUsers(retryCount + 1)
              }
            }, delay)
            return
          }

          // Don't report this to Sentry as it's expected behavior
          setError('Failed to fetch users after workspace change')
          return
        }

        // Handle other errors
        Sentry.captureMessage(`Error while querying users: ${error}`)
        setError('Failed to fetch workspace users')
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false)
        }
      }
    },
    [userDetails.id, chatClientService, workspaceID, fetchWorkspaceMembers, resetRetryCount]
  )

  // Effect to fetch users when dependencies change
  useEffect(() => {
    // Reset state immediately on dependency changes so stale users do not flash.
    resetLocalState()
    clearRetryTimeout()
    resetRetryCount()

    if (userDetails.id && chatClientService && workspaceID && !isWorkspacesLoading) {
      fetchUsers()
    }
  }, [
    userDetails.id,
    chatClientService,
    workspaceID,
    fetchUsers,
    clearRetryTimeout,
    resetRetryCount,
    isWorkspacesLoading,
    resetLocalState
  ])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      clearRetryTimeout()
    }
  }, [clearRetryTimeout])

  return {
    error,
    isLoading,
    mentionableUsers,
    refetch: () => {
      resetRetryCount()
      fetchUsers()
    },
    teamMembers
  }
}
